import { useEffect, useMemo, useState } from "react";
import {
  bumpEventStat,
  formatHubListingDate,
  getHubListingRsvp,
  hubPhoneTelHref,
  LISTING_TITLE_LINK_HINTS,
  respondHubListing,
  resolveListingTitleLink,
  shareHubListing,
  sourceLabel,
  useTheHubLive,
  venueMapsUrl,
} from "../../js/theHub";
import RoomMark from "./RoomMark";
import HubListingEditModal from "./HubListingEditModal";

function once(key) {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}

export default function EventCard({ item, session, groupId, onRemove, onListingAction }) {
  const canRemove =
    session?.role === "admin" || (session?.role === "broker" && item.group_id && item.group_id === groupId);
  const source = sourceLabel(item.source_id);
  const [actionNote, setActionNote] = useState("");
  const [urlEditOpen, setUrlEditOpen] = useState(false);
  const [hubPulse, setHubPulse] = useState(0);
  useTheHubLive(() => setHubPulse((n) => n + 1));

  const whenWhere = [formatHubListingDate(item.date), item.time, item.location].filter(Boolean).join(" · ");
  const titleLink = resolveListingTitleLink(item);
  const venueUrl = venueMapsUrl(item.address) || venueMapsUrl(item.location);
  const phone = String(item.phone || "").trim();
  const contact = String(item.contact || "").trim();
  const phoneHref = hubPhoneTelHref(phone);
  const rsvp = useMemo(
    () => getHubListingRsvp(session?.userId, item.id),
    [session?.userId, item.id, hubPulse]
  );

  useEffect(() => {
    if (once(`dh_thehub_seen_${item.id}`)) bumpEventStat(item.id, "views");
  }, [item.id]);

  useEffect(() => {
    if (!actionNote) return undefined;
    const timer = window.setTimeout(() => setActionNote(""), 6000);
    return () => window.clearTimeout(timer);
  }, [actionNote]);

  function scrollAfterAction(scrollTarget) {
    if (!scrollTarget) return;
    onListingAction?.(scrollTarget, item);
    if (typeof document === "undefined") return;
    document.getElementById(scrollTarget)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function onInterested() {
    if (!session?.userId) {
      setActionNote("Sign in to mark interest.");
      return;
    }
    try {
      const result = respondHubListing(session, item, "interested");
      setActionNote(result.message);
      if (result.posted) scrollAfterAction(result.scrollTarget);
    } catch (err) {
      setActionNote(err.message || "Could not save your response.");
    }
  }

  function onAttending() {
    if (!session?.userId) {
      setActionNote("Sign in to mark attendance.");
      return;
    }
    try {
      const result = respondHubListing(session, item, "attending");
      setActionNote(result.message);
      if (result.posted) scrollAfterAction(result.scrollTarget);
    } catch (err) {
      setActionNote(err.message || "Could not save your response.");
    }
  }

  async function onShare() {
    const result = await shareHubListing(item);
    if (result.message) setActionNote(result.message);
  }

  function onRemoveListing() {
    if (!onRemove) return;
    const ok = window.confirm(
      `Remove “${item.title}” from Hub listings?\n\nIt will disappear for everyone in this room.`
    );
    if (!ok) return;
    try {
      onRemove(item);
      setActionNote("Listing removed.");
    } catch (err) {
      setActionNote(err.message || "Could not remove that listing.");
    }
  }

  return (
    <li className={`the-hub-event${item.featured ? " is-featured" : ""}`}>
      <RoomMark
        roomId={item.category}
        label={item.title}
        initials={String(item.title || "EV").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "EV"}
        src={item.thumbnail}
        size={56}
      />
      <div>
        {item.featured ? <p className="the-hub-event__flag">Featured Event</p> : null}
        {titleLink.href ? (
          <strong>
            <a href={titleLink.href} target="_blank" rel="noreferrer noopener">
              {item.title}
            </a>
          </strong>
        ) : (
          <strong>{item.title}</strong>
        )}
        {titleLink.kind !== "none" ? (
          <p className="the-hub-event__listing-link muted">{LISTING_TITLE_LINK_HINTS[titleLink.kind]}</p>
        ) : null}
        {whenWhere ? <p>{whenWhere}</p> : null}
        {item.address ? (
          <p>
            <span className="the-hub-event__label">Address</span>{" "}
            {venueUrl ? (
              <a href={venueUrl} target="_blank" rel="noreferrer noopener">
                {item.address}
              </a>
            ) : (
              item.address
            )}
          </p>
        ) : null}
        {phone ? (
          <p>
            <span className="the-hub-event__label">Phone</span>{" "}
            {phoneHref ? <a href={phoneHref}>{phone}</a> : phone}
          </p>
        ) : null}
        {contact ? (
          <p>
            <span className="the-hub-event__label">Contact</span> {contact}
          </p>
        ) : null}
        {item.host ? <p className="muted">{item.host}</p> : null}
        {source && source !== item.host ? <p className="muted">{source}</p> : null}
        {item.description ? <p>{item.description}</p> : null}
        <p className="the-hub-event__tools">
          <button
            type="button"
            className={`text-btn${rsvp === "interested" ? " is-active" : ""}`}
            aria-pressed={rsvp === "interested"}
            onClick={onInterested}
          >
            {rsvp === "interested" ? "Interested ✓" : "Interested"}
          </button>
          <button
            type="button"
            className={`text-btn${rsvp === "attending" ? " is-active" : ""}`}
            aria-pressed={rsvp === "attending"}
            onClick={onAttending}
          >
            {rsvp === "attending" ? "I’ll attend ✓" : "I’ll attend"}
          </button>
          <button type="button" className="text-btn" onClick={onShare}>
            Share
          </button>
          {canRemove && onRemove ? (
            <button type="button" className="text-btn the-hub-event__remove" onClick={onRemoveListing}>
              Remove listing
            </button>
          ) : null}
          {session?.role === "admin" ? (
            <button type="button" className="text-btn" onClick={() => setUrlEditOpen(true)}>
              Edit contact & URL
            </button>
          ) : null}
        </p>
        {actionNote ? (
          <p className="the-hub-event__action-note" role="status">
            {actionNote}
          </p>
        ) : null}
      </div>
      {session?.role === "admin" ? (
        <HubListingEditModal
          item={item}
          session={session}
          isOpen={urlEditOpen}
          onClose={() => setUrlEditOpen(false)}
        />
      ) : null}
    </li>
  );
}
